import React, { FC, Fragment, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../shared/page-title/page-title.component';
import { Box, Fab } from '@mui/material';
import LabOpeningHours from './lab-opening-hours/lab-opening-hours.components';
import LabAvailability from './lab-availability/lab-availability.components';
import { LabSettings, Organization } from 'models/organization';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { updateLabContactAndAvailabilityTemplate } from 'apollo/templates/organization';
import { useMutation, useQuery } from '@apollo/client';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import moment from 'moment';
import { labSettingsTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

const AvailabilitySettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [inactiveIndices, setInactiveIndices] = useState<number[]>([]);
    const currentOrganization = currentOrgVar();
    const [selectedHolidayDays, setSelectedHolidayDays] = useState<Date[] | number[]>([]);
    const [selectedUnAvailableDates, setSelectedUnAvailableDates] = useState<Date[] | number[]>(
        currentOrganization.unavailableLabDates || [],
    );
    const [dayTimes, setDayTimes] = useState<Array<any>>([]);
    const [currentLab, setCurrentLab] = useState<Organization>();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const { loading } = useQuery(labSettingsTemplate, {
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ currentLab }) {
            setCurrentLab(currentLab);
            setSelectedHolidayDays(currentLab.holidayDays);
            setDayTimes(currentLab.openingHours);

            const indices = currentLab.openingHours
                .map((item: any, index: number) => (item.active ? -1 : index))
                .filter((index: number) => index !== -1)
                .map((index: number) => (index === 6 ? 0 : index + 1));
            setInactiveIndices(indices);
        },
    });

    const handleDayTimesChange = (newDayTimes: LabSettings) => {
        const newArray = Object.keys(newDayTimes).map((key) => {
            const closingHour = newDayTimes[key].closing.hour;
            const closingMinute = newDayTimes[key].closing.minute;
            const closingTimeString = `${closingHour}:${closingMinute}:00`;
            const dateString = moment().format('YYYY-MM-DD');
            const openingHour = newDayTimes[key].opening.hour;
            const openingMinute = newDayTimes[key].opening.minute;
            const openingTimeString = `${openingHour}:${openingMinute}:00`;
            const closingTime = new Date(`${dateString} ${closingTimeString}`);
            const openingTime = new Date(`${dateString} ${openingTimeString}`);

            return {
                active: newDayTimes[key].active,
                day: key,
                time: {
                    from: openingTime,
                    to: closingTime,
                },
            };
        });

        setDayTimes(newArray);

        const inactiveIndices = newArray
            .map((item, index) => (item.active ? -1 : index))
            .filter((index) => index !== -1)
            .map((index) => (index === 6 ? 0 : index + 1));
        setInactiveIndices(inactiveIndices);
    };

    const handleHolidayDays = (dates: Date[] | number[]) => {
        setSelectedHolidayDays(dates);
    };

    const handleUnavailableDays = (dates: Date[] | number[]) => {
        setSelectedUnAvailableDates(dates);
    };
    const [updateLabContactAndAvailability, { error }] = useMutation(updateLabContactAndAvailabilityTemplate, {
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('common:CHANGES_SAVED'),
            });
        },
    });

    const handleSaveChanges = async () => {
        const labContactAndAvailability = {
            unavailableLabDates: selectedUnAvailableDates,
            holidayDays: selectedHolidayDays,
            openingHours: dayTimes,
        };

        await updateLabContactAndAvailability({
            variables: { labContactAndAvailabilityData: labContactAndAvailability },
        });
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            <PageTitle headingStyle="h2" title={translate('lab-settings:OPENING_HOURS_AND_AVAILABILITY')} />
            {!loading && currentLab ? (
                <Fragment>
                    <Box mb={3}>
                        <LabOpeningHours onDayTimesChange={handleDayTimesChange} currentLab={currentLab} />
                    </Box>
                    <Box mb={3}>
                        <LabAvailability
                            currentLab={currentLab}
                            inactiveIndices={inactiveIndices}
                            onDayTimesChange={handleDayTimesChange}
                            onHolidayDaysChange={handleHolidayDays}
                            onUnavailableDaysChange={handleUnavailableDays}
                        />
                    </Box>

                    <Fab
                        sx={{ position: 'fixed', left: '50%', bottom: 20, zIndex: 100 }}
                        variant="extended"
                        onClick={handleSaveChanges}
                        color="primary"
                    >
                        <SaveOutlinedIcon sx={{ marginRight: 1 }} />
                        {translate('lab-settings:SAVE_CHANGES')}
                    </Fab>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default AvailabilitySettings;
